import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { useSwipeable } from 'react-swipeable';
import SecureImage from './SecureImage';
import StatusIndicator from './StatusIndicator';
import { digitsOnly } from '../utils/barcodeUtils';
import { resolveCardStatus } from '../utils/pidStatus';
import { commodityColor as colorForCommodity } from '../utils/commodityColor';

import {
  Overlay,
  Panel,
  Header,
  Title,
  CloseButton,
  TwoColumnSection,
  LeftColumn,
  RightColumn,
  HighlightedTitle,
  DetailGrid,
  InfoRow,
  InfoLabel,
  InfoValue,
  SectionDivider,
  CarouselNavButton,
  CarouselWrapper,
} from '../styles/PackageIndexStyles';

/* ================== Pill styling ================== */
const PillGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
`;

const CommodityPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--white);
  background: ${({ $bg }) => $bg};
  border: 2px solid ${({ $border }) => $border};
  box-shadow: 0 2px 6px var(--shadow-a15);
  user-select: none;
`;

/* ================== Color mapping ==================
   The commodity palette + hash fallback live in utils/commodityColor.js
   (imported above as colorForCommodity) — one source for the whole app.
   =================================================== */

const PluDetailPanel = ({ group, onClose }) => {
  const panelRef = useRef();
  const [current, setCurrent] = useState(0);
  const { LabelID, Items = [], Images = [], Picture } = group;
  const first = Items[0] || {};
  const { Description, GS1DataBar } = first;

  // Images with fallback
  const imagesToDisplay = useMemo(() => {
    if (Array.isArray(Images) && Images.length > 0) return Images;
    if (Picture) return [Picture];
    // Absolute blob URL provided by user for fallback
    return ['https://thevaultmedia.blob.core.windows.net/vault-images/PIDPLU/PLUFallback.webp'];
  }, [Images, Picture]);

  const max = imagesToDisplay.length;

  useEffect(() => {
    imagesToDisplay.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, [imagesToDisplay]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => setCurrent((c) => (c + 1) % max),
    onSwipedRight: () => setCurrent((c) => (c - 1 + max) % max),
    trackMouse: true,
  });

  const handleClickOutside = useCallback(
    (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    },
    [onClose]
  );

  const handleEscape = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [handleClickOutside, handleEscape]);

  // Unique, sorted commodities
  const commodities = useMemo(() => {
    const set = new Set();
    (Items || []).forEach(({ Commodity }) => {
      if (Commodity) set.add(String(Commodity));
    });
    return Array.from(set).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [Items]);

  return (
    <Overlay>
      <Panel ref={panelRef}>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minWidth: 0 }}>
            <Title>{LabelID}</Title>
            <StatusIndicator status={resolveCardStatus(group)} />
          </div>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </Header>

        <TwoColumnSection>
          <LeftColumn>
            <CarouselWrapper {...swipeHandlers}>
              <div style={{ position: 'relative' }}>
                {max > 1 && current > 0 && (
                  <CarouselNavButton
                    left
                    onClick={() => setCurrent((current - 1 + max) % max)}
                  >
                    &#8249;
                  </CarouselNavButton>
                )}
                {max > 1 && current < max - 1 && (
                  <CarouselNavButton
                    right
                    onClick={() => setCurrent((current + 1) % max)}
                  >
                    &#8250;
                  </CarouselNavButton>
                )}

                <SecureImage
                  src={imagesToDisplay[current]}
                  folder="PIDPLU"
                  alt={`PLU ${LabelID}`}
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '300px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                  }}
                />

                {max > 1 && (
                  <>
                    {/* Preload neighbors */}
                    <SecureImage
                      src={imagesToDisplay[(current + 1) % max]}
                      folder="PIDPLU"
                      alt=""
                      style={{ display: 'none' }}
                    />
                    <SecureImage
                      src={imagesToDisplay[(current - 1 + max) % max]}
                      folder="PIDPLU"
                      alt=""
                      style={{ display: 'none' }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '10px',
                        right: '12px',
                        color: 'var(--white)',
                        fontSize: '0.85rem',
                        background: 'var(--shadow-a35)',
                        padding: '2px 6px',
                        borderRadius: '6px',
                      }}
                    >
                      {current + 1} / {max}
                    </div>
                  </>
                )}
              </div>
            </CarouselWrapper>

            <DetailGrid>
              {Description && (
                <InfoRow>
                  <InfoLabel>Description</InfoLabel>
                  <InfoValue>{Description}</InfoValue>
                </InfoRow>
              )}
              {digitsOnly(GS1DataBar) && (
                <InfoRow>
                  <InfoLabel>GS1 Data Bar</InfoLabel>
                  <InfoValue>{digitsOnly(GS1DataBar)}</InfoValue>
                </InfoRow>
              )}
            </DetailGrid>
          </LeftColumn>

          <SectionDivider />

          <RightColumn style={{ overflowY: 'auto', maxHeight: '70vh' }}>
            <HighlightedTitle>Commodities</HighlightedTitle>
            <PillGrid>
              {commodities.map((commodity) => {
                const { bg, border } = colorForCommodity(commodity);
                return (
                  <CommodityPill key={commodity} $bg={bg} $border={border}>
                    {commodity}
                  </CommodityPill>
                );
              })}
            </PillGrid>
          </RightColumn>
        </TwoColumnSection>
      </Panel>
    </Overlay>
  );
};

export default React.memo(PluDetailPanel);
